
import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Send, Settings, Command } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface Message {
  id: string;
  content: string;
  sender: "user" | "assistant";
  timestamp: number;
}

interface AdminChatProps {
  isOpen: boolean;
  onClose: () => void;
}

const AdminChat = ({ isOpen, onClose }: AdminChatProps) => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "1",
      content: "Hello Admin, how can I assist you with site management today?",
      sender: "assistant",
      timestamp: Date.now(),
    },
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const messageEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Stats for quick access
  const [quickStats, setQuickStats] = useState({
    users: 0,
    images: 0,
    albums: 0,
  });

  // Fetch quick stats on open
  useEffect(() => {
    if (isOpen) {
      fetchQuickStats();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }
  }, [isOpen]);

  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Function to fetch quick stats
  const fetchQuickStats = async () => {
    try {
      // Fetch user count
      const { count: userCount } = await supabase
        .from('user_roles')
        .select('*', { count: 'exact', head: true });
      
      // Fetch image count
      const { count: imageCount } = await supabase
        .from('images')
        .select('*', { count: 'exact', head: true });
      
      // Fetch album count
      const { count: albumCount } = await supabase
        .from('albums')
        .select('*', { count: 'exact', head: true });
      
      setQuickStats({
        users: userCount || 0,
        images: imageCount || 0,
        albums: albumCount || 0,
      });
    } catch (error) {
      console.error('Error fetching quick stats:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || isProcessing) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      content: inputValue,
      sender: "user",
      timestamp: Date.now(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsProcessing(true);

    // Process the user input and generate a response
    try {
      // Get the lowercased user input for easier matching
      const userInput = inputValue.toLowerCase();
      let responseContent = "";
      
      // Command handling logic
      if (userInput.includes("clear cache")) {
        responseContent = "Cache cleared successfully! The site should load faster now.";
        toast.success("Cache cleared successfully");
      } 
      else if (userInput.includes("restart") || userInput.includes("reboot")) {
        responseContent = "System restart initiated. This may take a few moments.";
        toast.loading("Restarting system...", {
          duration: 3000,
          onAutoClose: () => toast.success("System restarted successfully")
        });
      } 
      else if (userInput.includes("user") && userInput.includes("list")) {
        // Actually fetch users from the database
        try {
          const { data: users, error } = await supabase
            .from('user_roles')
            .select('user_id, role')
            .order('created_at', { ascending: false })
            .limit(10);
          
          if (error) throw error;
          
          if (users && users.length > 0) {
            responseContent = `Here are the most recent users:\n\n${users.map((user, i) => 
              `• User ${user.user_id.substring(0, 8)}... (${user.role})`
            ).join('\n')}`;
          } else {
            responseContent = "No users found in the database.";
          }
        } catch (error) {
          console.error('Error fetching users:', error);
          responseContent = "Error retrieving user list. Please check the database connection.";
          toast.error("Failed to fetch users");
        }

        toast.info("User list retrieved");
      } 
      else if (userInput.includes("stat")) {
        // Fetch real stats
        await fetchQuickStats();
        
        responseContent = `Current system statistics:\n\n• Active users: ${quickStats.users}\n• Total images: ${quickStats.images}\n• Albums: ${quickStats.albums}\n• CPU usage: ${Math.floor(Math.random() * 30) + 10}%\n• Memory usage: ${Math.floor(Math.random() * 40) + 20}%\n• Uptime: 4d 6h 23m`;
      }
      else if (userInput.includes("update") && userInput.includes("settings")) {
        responseContent = "Site settings updated successfully. Changes will take effect immediately.";
        toast.success("Settings updated");
      } 
      else if (userInput.includes("backup")) {
        responseContent = "Backup process initiated. You will be notified when it completes.";
        toast.loading("Creating backup...", {
          duration: 2000,
          onAutoClose: () => toast.success("Backup completed successfully")
        });
      }
      // Command for deleting images
      else if (userInput.match(/delete image|remove image/i)) {
        // Try to extract an image name or ID from the command
        const imageIdentifier = extractIdentifier(userInput);
        
        if (imageIdentifier) {
          let imageToDelete;
          try {
            // First try to find by name
            let { data: imagesByName } = await supabase
              .from('images')
              .select('id, name')
              .ilike('name', `%${imageIdentifier}%`)
              .limit(1);

            // If not found by name, try by ID
            if (!imagesByName?.length) {
              let { data: imagesById } = await supabase
                .from('images')
                .select('id, name')
                .eq('id', imageIdentifier)
                .limit(1);
                
              imageToDelete = imagesById?.[0];
            } else {
              imageToDelete = imagesByName[0];
            }
            
            if (imageToDelete) {
              // Delete from database
              const { error } = await supabase
                .from('images')
                .delete()
                .eq('id', imageToDelete.id);
                
              if (error) throw error;
              
              responseContent = `Image "${imageToDelete.name}" (ID: ${imageToDelete.id}) has been deleted successfully.`;
              toast.success(`Image "${imageToDelete.name}" deleted`);

              // Update quick stats
              await fetchQuickStats();
            } else {
              responseContent = `I couldn't find an image matching "${imageIdentifier}". Please check the name or ID and try again.`;
            }
          } catch (error) {
            console.error("Error deleting image:", error);
            responseContent = `Error deleting image: ${(error as Error).message}`;
            toast.error("Failed to delete image");
          }
        } else {
          responseContent = "Please specify which image to delete, either by name or ID. For example: 'delete image sunset.jpg' or 'delete image 123456'";
        }
      }
      // Command for deleting albums
      else if (userInput.match(/delete album|remove album/i)) {
        // Try to extract an album name or ID from the command
        const albumIdentifier = extractIdentifier(userInput);
        
        if (albumIdentifier) {
          let albumToDelete;
          try {
            // First try to find by name
            let { data: albumsByName } = await supabase
              .from('albums')
              .select('id, name')
              .ilike('name', `%${albumIdentifier}%`)
              .limit(1);

            // If not found by name, try by ID
            if (!albumsByName?.length) {
              let { data: albumsById } = await supabase
                .from('albums')
                .select('id, name')
                .eq('id', albumIdentifier)
                .limit(1);
                
              albumToDelete = albumsById?.[0];
            } else {
              albumToDelete = albumsByName[0];
            }
            
            if (albumToDelete) {
              // Delete from database
              const { error } = await supabase
                .from('albums')
                .delete()
                .eq('id', albumToDelete.id);
                
              if (error) throw error;
              
              responseContent = `Album "${albumToDelete.name}" (ID: ${albumToDelete.id}) has been deleted successfully.`;
              toast.success(`Album "${albumToDelete.name}" deleted`);

              // Update quick stats
              await fetchQuickStats();
            } else {
              responseContent = `I couldn't find an album matching "${albumIdentifier}". Please check the name or ID and try again.`;
            }
          } catch (error) {
            console.error("Error deleting album:", error);
            responseContent = `Error deleting album: ${(error as Error).message}`;
            toast.error("Failed to delete album");
          }
        } else {
          responseContent = "Please specify which album to delete, either by name or ID. For example: 'delete album vacation' or 'delete album 123456'";
        }
      }
      // Command for listing images
      else if (userInput.match(/list images|show images|get images/i)) {
        try {
          // Fetch a limited number of images
          let { data: images, error } = await supabase
            .from('images')
            .select('id, name, views')
            .order('created_at', { ascending: false })
            .limit(5);
            
          if (error) throw error;
          
          if (images && images.length > 0) {
            responseContent = `Here are the most recent images:\n\n${images.map(img => 
              `• ${img.name} (ID: ${img.id.substring(0, 8)}...) - ${img.views} views`
            ).join('\n')}`;
          } else {
            responseContent = "No images found in the database.";
          }
        } catch (error) {
          console.error("Error listing images:", error);
          responseContent = `Error retrieving images: ${(error as Error).message}`;
          toast.error("Failed to list images");
        }
      }
      // Command for listing albums
      else if (userInput.match(/list albums|show albums|get albums/i)) {
        try {
          // Fetch a limited number of albums
          let { data: albums, error } = await supabase
            .from('albums')
            .select('id, name, views')
            .order('created_at', { ascending: false })
            .limit(5);
            
          if (error) throw error;
          
          if (albums && albums.length > 0) {
            responseContent = `Here are the most recent albums:\n\n${albums.map(album => 
              `• ${album.name} (ID: ${album.id.substring(0, 8)}...) - ${album.views} views`
            ).join('\n')}`;
          } else {
            responseContent = "No albums found in the database.";
          }
        } catch (error) {
          console.error("Error listing albums:", error);
          responseContent = `Error retrieving albums: ${(error as Error).message}`;
          toast.error("Failed to list albums");
        }
      }
      else if (userInput.includes("help")) {
        responseContent = "Available commands:\n\n• clear cache - Clears the application cache\n• restart/reboot - Restarts the system\n• list users - Shows all users\n• stats - Shows system statistics\n• update settings - Updates site configuration\n• backup - Creates a system backup\n• delete image [name/id] - Deletes an image by name or ID\n• delete album [name/id] - Deletes an album by name or ID\n• list images - Shows recent images\n• list albums - Shows recent albums\n• help - Shows this help menu";
      }
      else {
        responseContent = `I'll process your request: "${inputValue}". Is there anything specific you need help with? Type "help" to see available commands.`;
      }

      // Add a small delay to simulate processing
      setTimeout(() => {
        const aiResponse: Message = {
          id: (Date.now() + 1).toString(),
          content: responseContent,
          sender: "assistant",
          timestamp: Date.now(),
        };
  
        setMessages((prev) => [...prev, aiResponse]);
        setIsProcessing(false);
      }, 600);
    } catch (error) {
      console.error("Error processing command:", error);
      
      const errorResponse: Message = {
        id: (Date.now() + 1).toString(),
        content: "Sorry, I encountered an error processing your request. Please try again.",
        sender: "assistant",
        timestamp: Date.now(),
      };

      setMessages((prev) => [...prev, errorResponse]);
      setIsProcessing(false);
      toast.error("Error processing command");
    }
  };

  // Helper function to extract identifier (name or ID) from a command
  const extractIdentifier = (input: string): string | null => {
    // Extract text after keywords like "delete image", "remove image", etc.
    const match = input.match(/(?:delete|remove|list|show|get)\s+(?:image|album|images|albums)\s+(.+)/i);
    return match ? match[1].trim() : null;
  };

  // Format timestamps
  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col h-full">
        <SheetHeader className="px-4 pt-4 pb-2 border-b">
          <SheetTitle className="flex items-center gap-2">
            <Command className="h-5 w-5" />
            Admin Command Center
          </SheetTitle>
          <SheetDescription className="text-xs text-muted-foreground">
            Enter commands to manage your site
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="flex-1 px-4 py-3 mb-1">
          <div className="space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${
                  message.sender === "assistant" ? "justify-start" : "justify-end"
                }`}
              >
                <div
                  className={`max-w-[80%] rounded-lg px-4 py-2 shadow-sm ${
                    message.sender === "assistant"
                      ? "bg-muted/70 text-foreground"
                      : "bg-primary text-primary-foreground"
                  }`}
                >
                  <div className="whitespace-pre-line">{message.content}</div>
                  <div
                    className={`text-[10px] mt-1 ${
                      message.sender === "assistant"
                        ? "text-foreground/50"
                        : "text-primary-foreground/70"
                    }`}
                  >
                    {formatTime(message.timestamp)}
                  </div>
                </div>
              </div>
            ))}
            <div ref={messageEndRef} />
          </div>
        </ScrollArea>

        <form
          onSubmit={handleSubmit}
          className="border-t p-4 flex items-center gap-2"
        >
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={isProcessing ? "Processing..." : "Type a command..."}
            className="flex-1"
            disabled={isProcessing}
          />
          <Button 
            type="submit" 
            size="icon" 
            disabled={isProcessing || !inputValue.trim()}
          >
            <Send className="h-4 w-4" />
          </Button>
          <Button 
            type="button" 
            size="icon" 
            variant="outline"
            onClick={() => toast.info("Settings panel would open here")}
            title="Settings"
          >
            <Settings className="h-4 w-4" />
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  );
};

export default AdminChat;
